import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost, apiPut, apiDel, api } from '../lib/api.js';
import TopBar from '../components/TopBar.jsx';
import {
  Sparkles, Wand2, Play, Plus, Trash2, AlertTriangle, CheckCircle2,
  Send, Award, RefreshCw, Search, Layout, Image as ImageIcon, Globe,
  Zap, FileText, ShieldCheck, ShieldAlert, ShieldOff, ChevronRight,
} from 'lucide-react';
import toast from 'react-hot-toast';

const CHANNELS = [
  { key: 'google_search', label: 'Google Search', icon: Search },
  { key: 'google_pmax', label: 'Performance Max', icon: Zap },
  { key: 'meta', label: 'Meta (FB/IG)', icon: ImageIcon },
  { key: 'landing', label: 'Landing Copy', icon: Layout },
];

const PRODUCT_LINES = [
  { value: 'term', label: 'Term Life' },
  { value: 'iul', label: 'IUL' },
  { value: 'whole', label: 'Whole Life' },
  { value: 'final_expense', label: 'Final Expense' },
  { value: 'mortgage_protection', label: 'Mortgage Protection' },
  { value: 'annuity', label: 'Annuity' },
];

const TONES = ['urgent', 'reassuring', 'educational', 'direct_response', 'empathetic'];

export default function AdStudio() {
  const qc = useQueryClient();
  const [tab, setTab] = useState('generate'); // generate | briefs | results | winners
  const [activeBriefId, setActiveBriefId] = useState(null);
  const [activeGenId, setActiveGenId] = useState(null);

  const { data: status } = useQuery({
    queryKey: ['ad-studio-status'],
    queryFn: () => apiGet('/ad-studio/status'),
    refetchInterval: 30_000,
  });

  return (
    <div className="space-y-4">
      <TopBar title="Ad Studio" subtitle="AI ad generator · 4-channel · life-insurance compliant · Perplexity + Sonar" />

      {!status?.available && (
        <div className="panel p-4 border border-yellow-500/30 bg-yellow-500/5">
          <div className="flex items-start gap-2 text-sm">
            <AlertTriangle className="w-4 h-4 text-yellow-400 mt-0.5" />
            <div>
              <div className="font-medium text-yellow-200">Stub mode</div>
              <div className="text-xs text-white/60 mt-1">
                Set <code className="text-civix-cyan">PERPLEXITY_API_KEY</code> on Render to enable real Sonar research and ad generation.
                Until then the studio returns deterministic stub variants so you can preview the flow end-to-end.
                Get a key at <a href="https://www.perplexity.ai/settings/api" target="_blank" rel="noreferrer" className="text-civix-cyan underline">perplexity.ai/settings/api</a>.
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center gap-1 border-b border-white/10">
        {[
          { k: 'generate', l: 'Generate', i: Wand2 },
          { k: 'briefs', l: 'Saved Briefs', i: FileText },
          { k: 'results', l: 'Results', i: Sparkles },
          { k: 'winners', l: 'Winners', i: Award },
        ].map((t) => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            className={`flex items-center gap-2 px-3 py-2 text-sm border-b-2 -mb-px ${
              tab === t.k ? 'border-civix-cyan text-white' : 'border-transparent text-white/50 hover:text-white/80'
            }`}
          >
            <t.i className="w-4 h-4" /> {t.l}
          </button>
        ))}
        {status?.counts && (
          <div className="ml-auto flex items-center gap-3 text-xs text-white/40 px-3">
            <span>{status.counts.briefs} briefs</span>
            <span>·</span>
            <span>{status.counts.generations} runs</span>
            <span>·</span>
            <span>{status.counts.ads} ads</span>
            <span>·</span>
            <span className="text-civix-cyan">{status.counts.winners} winners</span>
          </div>
        )}
      </div>

      {tab === 'generate' && <GenerateTab onComplete={(genId) => { setActiveGenId(genId); setTab('results'); }} activeBriefId={activeBriefId} />}
      {tab === 'briefs' && <BriefsTab onSelect={(id) => { setActiveBriefId(id); setTab('generate'); }} />}
      {tab === 'results' && <ResultsTab activeGenId={activeGenId} setActiveGenId={setActiveGenId} />}
      {tab === 'winners' && <WinnersTab />}
    </div>
  );
}

// ─── Generate Tab ─────────────────────────────────────────────────────────

function GenerateTab({ onComplete, activeBriefId }) {
  const [form, setForm] = useState({
    name: 'New Brief',
    productLine: 'term',
    tone: 'reassuring',
    persona: { ageMin: 30, ageMax: 65, lifeEvent: '', incomeBand: '', otherTraits: '' },
    offer: '',
    keyBenefits: [],
    targetStates: [],
    complianceLevel: 'strict',
    notes: '',
  });
  const [config, setConfig] = useState({
    channels: ['google_search', 'google_pmax', 'meta', 'landing'],
    variantsPerChannel: 5,
    researchModel: 'sonar-pro',
    emitModel: 'sonar',
  });
  const [generating, setGenerating] = useState(false);
  const [keyBenefitInput, setKeyBenefitInput] = useState('');
  const [stateInput, setStateInput] = useState('');

  // Load brief if one is active
  useEffect(() => {
    if (!activeBriefId) return;
    apiGet(`/ad-studio/briefs/${activeBriefId}`).then((r) => {
      if (r?.brief) {
        setForm({
          name: r.brief.name || 'Brief',
          productLine: r.brief.productLine || 'term',
          tone: r.brief.tone || 'reassuring',
          persona: r.brief.persona || {},
          offer: r.brief.offer || '',
          keyBenefits: r.brief.keyBenefits || [],
          targetStates: r.brief.targetStates || [],
          complianceLevel: r.brief.complianceLevel || 'strict',
          notes: r.brief.notes || '',
        });
        if (r.brief.lastConfig) setConfig({ ...config, ...r.brief.lastConfig });
      }
    }).catch(() => {});
    // eslint-disable-next-line
  }, [activeBriefId]);

  function setField(k, v) { setForm((f) => ({ ...f, [k]: v })); }
  function setPersona(k, v) { setForm((f) => ({ ...f, persona: { ...f.persona, [k]: v } })); }
  function toggleChannel(c) {
    setConfig((cfg) => ({
      ...cfg,
      channels: cfg.channels.includes(c) ? cfg.channels.filter((x) => x !== c) : [...cfg.channels, c],
    }));
  }
  function addBenefit() {
    if (!keyBenefitInput.trim()) return;
    setField('keyBenefits', [...form.keyBenefits, keyBenefitInput.trim()]);
    setKeyBenefitInput('');
  }
  function addState() {
    const s = stateInput.trim().toUpperCase();
    if (s.length !== 2 || form.targetStates.includes(s)) return;
    setField('targetStates', [...form.targetStates, s]);
    setStateInput('');
  }

  async function saveBrief() {
    try {
      const r = activeBriefId
        ? await apiPut(`/ad-studio/briefs/${activeBriefId}`, form)
        : await apiPost('/ad-studio/briefs', form);
      toast.success('Brief saved');
      return r.brief;
    } catch (e) {
      toast.error(e.message);
    }
  }

  async function generate() {
    setGenerating(true);
    try {
      const payload = {
        brief: { ...form },
        ...config,
      };
      if (activeBriefId) payload.briefId = activeBriefId;
      const r = await apiPost('/ad-studio/generate', payload);
      toast.success(`Generated ${r.ads?.length || 0} ads in ${(r.generation?.durationMs / 1000).toFixed(1)}s`);
      onComplete(r.generation?._id);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setGenerating(false);
    }
  }

  return (
    <div className="grid lg:grid-cols-3 gap-4">
      <div className="lg:col-span-2 panel p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-white/70">
            <Wand2 className="w-4 h-4" /> Brief
          </div>
          <input
            className="input text-base font-medium flex-1 ml-3 max-w-md"
            placeholder="Brief name (e.g. Texas New Homeowners — MP)"
            value={form.name}
            onChange={(e) => setField('name', e.target.value)}
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Product Line">
            <select className="input w-full" value={form.productLine} onChange={(e) => setField('productLine', e.target.value)}>
              {PRODUCT_LINES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </Field>
          <Field label="Tone">
            <select className="input w-full" value={form.tone} onChange={(e) => setField('tone', e.target.value)}>
              {TONES.map((t) => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
            </select>
          </Field>
          <Field label="Age Range">
            <div className="flex items-center gap-2">
              <input type="number" className="input w-20" value={form.persona.ageMin || 30} onChange={(e) => setPersona('ageMin', +e.target.value)} />
              <span className="text-white/40">to</span>
              <input type="number" className="input w-20" value={form.persona.ageMax || 65} onChange={(e) => setPersona('ageMax', +e.target.value)} />
            </div>
          </Field>
          <Field label="Life Event">
            <input className="input w-full" placeholder="e.g. new_parent, homeowner, retiring" value={form.persona.lifeEvent || ''} onChange={(e) => setPersona('lifeEvent', e.target.value)} />
          </Field>
          <Field label="Income Band">
            <input className="input w-full" placeholder="$50k-$150k" value={form.persona.incomeBand || ''} onChange={(e) => setPersona('incomeBand', e.target.value)} />
          </Field>
          <Field label="Compliance Level">
            <select className="input w-full" value={form.complianceLevel} onChange={(e) => setField('complianceLevel', e.target.value)}>
              <option value="strict">Strict (recommended)</option>
              <option value="moderate">Moderate</option>
              <option value="off">Off</option>
            </select>
          </Field>
        </div>

        <Field label="Offer / Hook">
          <input className="input w-full" placeholder="e.g. $500K from $18/mo, free quote in 60s" value={form.offer} onChange={(e) => setField('offer', e.target.value)} />
        </Field>

        <Field label="Key Benefits">
          <div className="flex gap-2">
            <input
              className="input flex-1"
              placeholder="No medical exam"
              value={keyBenefitInput}
              onChange={(e) => setKeyBenefitInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addBenefit())}
            />
            <button onClick={addBenefit} className="px-3 rounded-md bg-white/5 hover:bg-white/10 text-sm">Add</button>
          </div>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {form.keyBenefits.map((b, i) => (
              <span key={i} className="text-xs px-2 py-1 rounded-sm bg-white/5 flex items-center gap-1">
                {b}
                <button onClick={() => setField('keyBenefits', form.keyBenefits.filter((_, j) => j !== i))} className="text-white/40 hover:text-red-300">×</button>
              </span>
            ))}
          </div>
        </Field>

        <Field label="Target States (2-letter codes)">
          <div className="flex gap-2">
            <input
              className="input flex-1"
              placeholder="TX"
              maxLength={2}
              value={stateInput}
              onChange={(e) => setStateInput(e.target.value.toUpperCase())}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addState())}
            />
            <button onClick={addState} className="px-3 rounded-md bg-white/5 hover:bg-white/10 text-sm">Add</button>
          </div>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {form.targetStates.map((s, i) => (
              <span key={s} className="text-xs px-2 py-1 rounded-sm bg-civix-purple/20 text-civix-cyan flex items-center gap-1">
                {s}
                <button onClick={() => setField('targetStates', form.targetStates.filter((_, j) => j !== i))} className="text-white/40 hover:text-red-300">×</button>
              </span>
            ))}
            {form.targetStates.length === 0 && <span className="text-xs text-white/30">empty = all licensed states</span>}
          </div>
        </Field>

        <Field label="Notes for the AI (optional)">
          <textarea className="input w-full min-h-[80px]" placeholder="Anything else the AI should know..." value={form.notes} onChange={(e) => setField('notes', e.target.value)} />
        </Field>
      </div>

      <div className="space-y-4">
        <div className="panel p-5 space-y-4">
          <div className="text-sm text-white/70 flex items-center gap-2">
            <Zap className="w-4 h-4" /> Generation Config
          </div>
          <Field label="Channels">
            <div className="grid grid-cols-2 gap-2">
              {CHANNELS.map((c) => (
                <button
                  key={c.key}
                  onClick={() => toggleChannel(c.key)}
                  className={`text-xs p-2 rounded-md border flex items-center gap-2 ${
                    config.channels.includes(c.key)
                      ? 'border-civix-cyan bg-civix-cyan/10 text-white'
                      : 'border-white/10 text-white/50 hover:text-white'
                  }`}
                >
                  <c.icon className="w-3 h-3" /> {c.label}
                </button>
              ))}
            </div>
          </Field>
          <Field label="Variants per channel">
            <input
              type="number"
              min="1"
              max="10"
              className="input w-full"
              value={config.variantsPerChannel}
              onChange={(e) => setConfig({ ...config, variantsPerChannel: +e.target.value })}
            />
          </Field>
          <Field label="Research model (Perplexity)">
            <select className="input w-full" value={config.researchModel} onChange={(e) => setConfig({ ...config, researchModel: e.target.value })}>
              <option value="sonar-pro">sonar-pro (recommended)</option>
              <option value="sonar">sonar (cheaper)</option>
              <option value="sonar-reasoning">sonar-reasoning</option>
              <option value="sonar-reasoning-pro">sonar-reasoning-pro</option>
            </select>
          </Field>
          <Field label="Emit model">
            <select className="input w-full" value={config.emitModel} onChange={(e) => setConfig({ ...config, emitModel: e.target.value })}>
              <option value="sonar">sonar (fast, cheap)</option>
              <option value="sonar-pro">sonar-pro</option>
              <option value="sonar-reasoning">sonar-reasoning</option>
            </select>
          </Field>
        </div>

        <div className="panel p-5 space-y-3">
          <button
            onClick={generate}
            disabled={generating || config.channels.length === 0}
            className="w-full px-4 py-3 rounded-md bg-linear-to-r from-civix-purple to-civix-cyan font-medium disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {generating ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {generating ? 'Generating…' : `Generate ${config.channels.length * config.variantsPerChannel} ads`}
          </button>
          <button
            onClick={saveBrief}
            className="w-full px-4 py-2 rounded-md border border-white/10 hover:border-white/20 text-sm"
          >
            Save brief
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-xs uppercase tracking-wider text-white/50 mb-1.5">{label}</div>
      {children}
    </label>
  );
}

// ─── Briefs Tab ──────────────────────────────────────────────────────────

function BriefsTab({ onSelect }) {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ['ad-studio-briefs'],
    queryFn: () => apiGet('/ad-studio/briefs'),
  });
  async function remove(id) {
    if (!confirm('Archive this brief?')) return;
    await apiDel(`/ad-studio/briefs/${id}`);
    toast.success('Archived');
    qc.invalidateQueries({ queryKey: ['ad-studio-briefs'] });
  }
  const items = data?.items || [];
  if (!items.length) {
    return <div className="panel p-8 text-center text-white/50">No saved briefs yet. Create one in the Generate tab.</div>;
  }
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
      {items.map((b) => (
        <div key={b._id} className="panel p-4 hover:border-civix-cyan/40 cursor-pointer" onClick={() => onSelect(b._id)}>
          <div className="flex items-start justify-between gap-2">
            <div className="font-medium truncate">{b.name}</div>
            <button onClick={(e) => { e.stopPropagation(); remove(b._id); }} className="text-white/30 hover:text-red-400">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="text-xs text-white/50 mt-1 capitalize">{b.productLine?.replace('_', ' ')} · {b.tone?.replace('_', ' ')}</div>
          {b.offer && <div className="text-xs text-white/70 mt-2 italic">"{b.offer}"</div>}
          <div className="flex gap-1.5 mt-3 text-xs text-white/40">
            {(b.targetStates || []).slice(0, 6).map((s) => (
              <span key={s} className="px-1.5 py-0.5 rounded-sm bg-civix-purple/20 text-civix-cyan">{s}</span>
            ))}
            {(b.targetStates || []).length > 6 && <span>+{b.targetStates.length - 6}</span>}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Results Tab ─────────────────────────────────────────────────────────

function ResultsTab({ activeGenId, setActiveGenId }) {
  const qc = useQueryClient();
  const { data: gens } = useQuery({
    queryKey: ['ad-studio-generations'],
    queryFn: () => apiGet('/ad-studio/generations'),
  });
  const { data: detail } = useQuery({
    queryKey: ['ad-studio-generation', activeGenId],
    queryFn: () => apiGet(`/ad-studio/generations/${activeGenId}`),
    enabled: !!activeGenId,
  });

  // Auto-select most recent if none selected
  useEffect(() => {
    if (!activeGenId && gens?.items?.length) setActiveGenId(gens.items[0]._id);
  }, [gens, activeGenId, setActiveGenId]);

  async function pushAll() {
    if (!detail?.generation?._id) return;
    if (!confirm('Push all approved ads to their native platforms as paused drafts?')) return;
    try {
      const r = await apiPost(`/ad-studio/generations/${detail.generation._id}/push`, {});
      toast.success(`Pushed ${r.pushed}, skipped ${r.skipped}, errors ${r.errors}`);
      qc.invalidateQueries({ queryKey: ['ad-studio-generation', activeGenId] });
    } catch (e) {
      toast.error(e.message);
    }
  }

  if (!gens?.items?.length) {
    return <div className="panel p-8 text-center text-white/50">No generations yet. Run one from the Generate tab.</div>;
  }
  return (
    <div className="grid lg:grid-cols-4 gap-4">
      <div className="space-y-2">
        <div className="text-xs uppercase tracking-wider text-white/50">Recent runs</div>
        {(gens.items || []).map((g) => (
          <button
            key={g._id}
            onClick={() => setActiveGenId(g._id)}
            className={`w-full text-left p-3 rounded-md border ${activeGenId === g._id ? 'border-civix-cyan bg-civix-cyan/5' : 'border-white/10 hover:border-white/20'}`}
          >
            <div className="text-xs text-white/40">{new Date(g.createdAt).toLocaleString()}</div>
            <div className="text-sm mt-1">{g.channels?.length || 0} channels · {g.generatedAdIds?.length || 0} ads</div>
            <div className="text-xs text-white/50 mt-1">
              {g.status} {g.stub && <span className="text-yellow-300">· stub</span>}
            </div>
          </button>
        ))}
      </div>

      <div className="lg:col-span-3 space-y-3">
        {detail?.generation && (
          <>
            <div className="panel p-4 flex items-center justify-between">
              <div>
                <div className="text-sm">
                  Generated {detail.ads?.length || 0} ads · {(detail.generation.durationMs / 1000).toFixed(1)}s · ${(detail.generation.estimatedCostUsd || 0).toFixed(4)}
                </div>
                <div className="text-xs text-white/50 mt-1">
                  research: {detail.generation.researchModel} · emit: {detail.generation.emitModel}
                </div>
              </div>
              <button onClick={pushAll} className="px-4 py-2 rounded-md bg-linear-to-r from-civix-purple to-civix-cyan text-sm font-medium flex items-center gap-2">
                <Send className="w-4 h-4" /> Push all to platforms
              </button>
            </div>

            {detail.generation.researchSummary && (
              <div className="panel p-4 text-xs text-white/60 whitespace-pre-wrap max-h-40 overflow-y-auto">
                <div className="text-white/80 font-medium mb-1">Research summary</div>
                {detail.generation.researchSummary}
                {(detail.generation.researchCitations || []).length > 0 && (
                  <div className="mt-2 pt-2 border-t border-white/10 text-white/40">
                    Sources: {detail.generation.researchCitations.map((c, i) => (
                      <a key={i} href={c.url} target="_blank" rel="noreferrer" className="text-civix-cyan underline mr-2">[{i + 1}]</a>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-3">
              {(detail.ads || []).map((ad) => <AdCard key={ad._id} ad={ad} onChanged={() => qc.invalidateQueries({ queryKey: ['ad-studio-generation', activeGenId] })} />)}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Ad Card (per-channel preview) ───────────────────────────────────────

function AdCard({ ad, onChanged }) {
  const [pushing, setPushing] = useState(false);
  const channelMeta = CHANNELS.find((c) => c.key === ad.channel);
  const Icon = channelMeta?.icon || Search;

  async function push() {
    setPushing(true);
    try {
      await apiPost(`/ad-studio/ads/${ad._id}/push`, { force: !ad.compliance?.pass });
      toast.success('Pushed as paused draft');
      onChanged();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setPushing(false);
    }
  }

  async function archive() {
    try {
      await api.patch(`/ad-studio/ads/${ad._id}`, { archivedAt: new Date(), status: 'archived' });
      toast.success('Archived');
      onChanged();
    } catch (e) {
      toast.error(e.message);
    }
  }

  const flags = ad.compliance?.flags || [];
  const errors = flags.filter((f) => f.severity === 'error');
  const warns = flags.filter((f) => f.severity === 'warn');

  return (
    <div className="panel p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-civix-cyan" />
          <span className="text-xs uppercase tracking-wider text-white/60">{channelMeta?.label}</span>
          {ad.hook && <span className="text-xs text-white/40">· {ad.hook}</span>}
        </div>
        <ComplianceBadge compliance={ad.compliance} />
      </div>

      <ChannelPreview ad={ad} />

      {(errors.length > 0 || warns.length > 0) && (
        <div className="border border-white/10 rounded-md p-2 space-y-1 text-xs">
          {errors.map((f, i) => (
            <div key={`e${i}`} className="flex items-start gap-2 text-red-300">
              <ShieldAlert className="w-3 h-3 mt-0.5 shrink-0" />
              <div><span className="font-medium">{f.rule}:</span> {f.message} <span className="text-white/40">— "{f.excerpt}"</span></div>
            </div>
          ))}
          {warns.map((f, i) => (
            <div key={`w${i}`} className="flex items-start gap-2 text-yellow-300">
              <ShieldAlert className="w-3 h-3 mt-0.5 shrink-0" />
              <div><span className="font-medium">{f.rule}:</span> {f.message} <span className="text-white/40">— "{f.excerpt}"</span></div>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between gap-2 pt-2 border-t border-white/5">
        <span className={`text-xs px-2 py-0.5 rounded ${
          ad.status === 'pushed' ? 'bg-green-500/20 text-green-300' :
          ad.status === 'winner' ? 'bg-civix-cyan/20 text-civix-cyan' :
          ad.status === 'loser' ? 'bg-red-500/20 text-red-300' :
          'bg-white/5 text-white/60'
        }`}>{ad.status}</span>
        <div className="flex gap-2">
          <button onClick={archive} className="text-xs px-2 py-1 rounded-sm border border-white/10 hover:border-red-300/40 hover:text-red-300">Archive</button>
          <button
            onClick={push}
            disabled={pushing || ad.status === 'pushed' || ad.status === 'live'}
            className="text-xs px-3 py-1 rounded-sm bg-linear-to-r from-civix-purple to-civix-cyan disabled:opacity-50 flex items-center gap-1"
          >
            <Send className="w-3 h-3" /> {pushing ? '…' : 'Push'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ComplianceBadge({ compliance }) {
  if (!compliance) return null;
  if (compliance.level === 'off') {
    return <span className="text-xs flex items-center gap-1 text-white/40"><ShieldOff className="w-3 h-3" /> off</span>;
  }
  if (compliance.pass) {
    return <span className="text-xs flex items-center gap-1 text-green-400"><ShieldCheck className="w-3 h-3" /> pass</span>;
  }
  return <span className="text-xs flex items-center gap-1 text-red-400"><ShieldAlert className="w-3 h-3" /> blocked</span>;
}

// ─── Channel-specific previews ───────────────────────────────────────────

function ChannelPreview({ ad }) {
  const a = ad.assets || {};
  if (ad.channel === 'google_search') {
    const h = (a.headlines || []).slice(0, 3).map((h) => h.text).filter(Boolean);
    const d = (a.descriptions || []).slice(0, 2).map((d) => d.text).filter(Boolean);
    return (
      <div className="bg-white/5 border border-white/10 rounded-md p-3 space-y-1 text-sm">
        <div className="text-civix-cyan text-xs">Sponsored · civix-quotes.com{a.path1 ? `/${a.path1}` : ''}{a.path2 ? `/${a.path2}` : ''}</div>
        <div className="text-base text-white">{h.join(' | ')}</div>
        <div className="text-xs text-white/70">{d.join(' ')}</div>
        {(a.callouts || []).length > 0 && (
          <div className="text-xs text-white/40 mt-2">{(a.callouts || []).slice(0, 4).join(' · ')}</div>
        )}
        <div className="text-xs text-white/30 mt-1">+{(a.headlines || []).length - 3} more headlines · {(a.sitelinks || []).length} sitelinks</div>
      </div>
    );
  }
  if (ad.channel === 'meta') {
    return (
      <div className="bg-white/5 border border-white/10 rounded-md p-3 space-y-2 text-sm">
        <div className="text-xs text-white/40">CIVIX Insurance · Sponsored</div>
        <div className="text-sm whitespace-pre-wrap">{a.primaryText}</div>
        <div className="bg-white/5 rounded-sm p-2">
          <div className="aspect-[1.91/1] bg-linear-to-br from-civix-purple/30 to-civix-cyan/30 rounded-sm mb-2 flex items-center justify-center text-xs text-white/40">
            <ImageIcon className="w-6 h-6" />
            <span className="ml-2 italic">{a.imagePrompt?.slice(0, 60) || 'image prompt'}</span>
          </div>
          <div className="font-medium">{a.headline}</div>
          <div className="text-xs text-white/50">{a.description}</div>
        </div>
        <button className="w-full text-xs py-1.5 rounded-sm bg-civix-cyan/20 text-civix-cyan">{a.cta || 'Learn More'}</button>
      </div>
    );
  }
  if (ad.channel === 'google_pmax') {
    return (
      <div className="bg-white/5 border border-white/10 rounded-md p-3 space-y-2 text-sm">
        <div className="text-xs uppercase tracking-wider text-white/40">PMax Asset Group</div>
        <div className="space-y-1">
          {(a.shortHeadlines || []).slice(0, 3).map((h, i) => <div key={`s${i}`} className="text-sm">• {h}</div>)}
        </div>
        <div className="text-xs text-white/60 italic">{(a.longHeadlines || [])[0]}</div>
        <div className="text-xs text-white/40">{(a.descriptions || [])[0]}</div>
        <div className="text-xs text-white/30 mt-2">{(a.imagePrompts || []).length} image prompts · {a.videoScript ? '1 video script' : 'no video'}</div>
      </div>
    );
  }
  if (ad.channel === 'landing') {
    return (
      <div className="bg-white/5 border border-white/10 rounded-md p-3 space-y-2 text-sm">
        <div className="text-base font-semibold">{a.hero}</div>
        <div className="text-xs text-white/70">{a.subhead}</div>
        <ul className="space-y-1 text-xs text-white/80">
          {(a.bullets || []).slice(0, 4).map((b, i) => <li key={i}>✓ {b}</li>)}
        </ul>
        <div className="flex gap-2 pt-2">
          <span className="text-xs px-2 py-1 rounded-sm bg-civix-cyan/20 text-civix-cyan">{a.primaryCta}</span>
          {a.secondaryCta && <span className="text-xs px-2 py-1 rounded-sm border border-white/10">{a.secondaryCta}</span>}
        </div>
      </div>
    );
  }
  return null;
}

// ─── Winners Tab ─────────────────────────────────────────────────────────

function WinnersTab() {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ['ad-studio-winners'],
    queryFn: () => apiGet('/ad-studio/winners'),
  });
  async function runLoop() {
    try {
      const r = await apiPost('/ad-studio/performance/run', {});
      toast.success(`Scored ${r.scored} · ${r.winners} winners · ${r.losers} losers`);
      qc.invalidateQueries({ queryKey: ['ad-studio-winners'] });
    } catch (e) {
      toast.error(e.message);
    }
  }
  const items = data?.items || [];
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between panel p-4">
        <div>
          <div className="text-sm text-white/70 flex items-center gap-2"><Award className="w-4 h-4" /> Winners (sorted by score)</div>
          <div className="text-xs text-white/40 mt-1">Auto-tagged by the weekly cron — Sunday 5am.</div>
        </div>
        <button onClick={runLoop} className="px-3 py-2 rounded-md border border-white/10 hover:border-white/20 text-sm flex items-center gap-2">
          <RefreshCw className="w-3.5 h-3.5" /> Score now
        </button>
      </div>
      {items.length === 0 ? (
        <div className="panel p-8 text-center text-white/50">No scored ads yet. Push ads to platforms, wait 7 days, then run the loop.</div>
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {items.map((ad) => (
            <div key={ad._id} className="panel p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-xs uppercase tracking-wider text-white/60">{ad.channel.replace('_', ' ')}</span>
                  <span className={`text-xs px-2 py-0.5 rounded ${
                    ad.status === 'winner' ? 'bg-civix-cyan/20 text-civix-cyan' : 'bg-white/5'
                  }`}>{ad.status}</span>
                </div>
                <div className="text-2xl font-bold text-civix-cyan">{ad.performance?.score || 0}</div>
              </div>
              <ChannelPreview ad={ad} />
              <div className="grid grid-cols-3 gap-2 text-xs mt-2">
                <div><span className="text-white/40">ROAS:</span> {(ad.performance?.roas || 0).toFixed(2)}x</div>
                <div><span className="text-white/40">Sold:</span> {ad.performance?.sold || 0}</div>
                <div><span className="text-white/40">CPA:</span> ${((ad.performance?.cpaCents || 0) / 100).toFixed(2)}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
